import React from 'react';
import { HashRouter as Router, Route, Switch } from 'react-router-dom';
import './sass/Main.scss';
import { connect } from "react-redux";
import i18n from "./i18n";
import NetworkDetector from "./Layout/NetworkDetector";
import Admin from "./Layout/Admin";
import ErrorBoundary from './Components/ErrorBoundary';
import { PrivateRouteWithRedux } from "./PrivateRoute";
import { VerificationEmailScreensWithState } from "./Components/Auth/VerificationEmailScreen";
import { AuthRouter } from "./Components/Auth/AuthRouter";
import Terms from "./Components/Legal/Terms";
import TermsHi from "./Components/Legal/TermsHi";
import PrivacyPolicyHi from 'Components/Legal/PrivacyPolicyHi';
import Privacy from "./Components/Legal/Privacy";
import Faq from "./Components/Legal/Faq";
import FaqHi from "./Components/Legal/FaqHi";
import Support from "./Components/Legal/Support";
import Support_hi from "./Components/Legal/Support_hi";
import CookiePolicyHi from 'Components/Legal/CookiePolicyHi';
import CookiePolicy from 'Components/Legal/CookiePolicy';
import RewardsProgramTermsHi from 'Components/Legal/RewardsProgramTermsHi';
import RewardsProgramTerms from 'Components/Legal/RewardsProgramTerms';
import TermsJp from "./Components/Legal/TermsJp";
import PrivacyJp from 'Components/Legal/PrivacyJp';
import SupportJp from "./Components/Legal/SupportJp";
import CookiePolicyJp from 'Components/Legal/CookiePolicyJp';
import RewardsProgramTermsJp from 'Components/Legal/RewardsProgramTermsJp';
import SurveyCompletedCallBackPage from "./Components/SurveyCallbackPages/SurveyCompletedCallBackPage";
import SurveySpeederLimitTerminate from "./Components/SurveyCallbackPages/SurveySpeederLimitTerminate";
import SurveyOverQuotaCallbackPage from "./Components/SurveyCallbackPages/SurveyOverQuotaCallbackPage";
import SurveyTerminatedCallbackPage from "./Components/SurveyCallbackPages/SurveyTerminatedCallbackPage";
import SurveyQualityTerminatedCallbackPage from "./Components/SurveyCallbackPages/SurveyQualityTerminatedCallbackPage";
import { LinkViewWithState } from "./Components/Referrals/LinkView";
import { requestForToken, foreGroundMessage } from './firebase';
import PartnerSurvey from "./Components/Partners/PartnerSurvey";
import { ResetPasswordFormWithState } from "./Components/Auth/UpdateResetPasswordForm";
import { SurveyAttemptMessageWithState } from "./Components/Surveys/SurveyAlreadyAttempted";
import { SurveyUnavailableMessageWithState } from "./Components/Surveys/SurveyUnavailable";
import SurveyHold from 'Components/SurveyHold/SurveyHold';
import SurveyEntry from './Components/Surveys/SurveyEntry';
import PreScreenQuesPage from 'Components/PreScreenQuesPage/PreScreenQuesPage';
import PartnerPreScreenQuesPage from 'Components/PreScreenQuesPage/PartnerPreSceenQuePage';
import PartnerSurveyHold from 'Components/SurveyHold/PartnerSurveyHold';
import ScrollToTop from './Components/Common/ScrollToTop';

try {
  requestForToken();
} catch (error) {
  console.log("Notification permission denied or error:", error);
}
foreGroundMessage();
class App extends React.Component<any, any> {
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;

  componentDidMount() {
    window.addEventListener('mousemove', this.handleActivity);
    window.addEventListener('keypress', this.handleActivity);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.handleActivity();
    this.syncLanguage();
  }

  componentDidUpdate() {
    this.syncLanguage();
  }

  // Keep i18next in sync with the Redux-persisted language (handles boot/refresh + switches).
  syncLanguage = () => {
    const reduxLang = this.props.adminUser?.adminUser?.language;
    if (reduxLang && i18n.language !== reduxLang) {
      i18n.changeLanguage(reduxLang);
    }
  };

  componentWillUnmount() {
    window.removeEventListener('mousemove', this.handleActivity);
    window.removeEventListener('keypress', this.handleActivity);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.clearLogoutTimer();
  }

  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.handleActivity();
    }
  };

  handleActivity = () => {
    this.clearLogoutTimer();
    this.startLogoutTimer();
  };

  startLogoutTimer = () => {
    const role = this.props.adminUser?.adminUser?.role ?? "finance";
    const specialLogout = { "admin": 30, "sub-admin": 30, "pm": 60, "panelist": 15, "finance": 15 };
    this.logoutTimer = setTimeout(() => {
      const SESSION_EXPIRY_MS = 10 * 60 * 1000;
      const lastUserActivity: any = localStorage.getItem("lastUserActivity");
      const now: any = Date.now();
      if (lastUserActivity && now - lastUserActivity > SESSION_EXPIRY_MS) {
        // Session-expiry logout: preserve the language preference across the
        // localStorage wipe so the login page keeps the user's chosen language.
        let savedLang: string | null = null;
        try { savedLang = localStorage.getItem('userLanguagePreference'); } catch (e) { /* storage unavailable — non-fatal */ }
        localStorage.clear();
        try { if (savedLang) { localStorage.setItem('userLanguagePreference', savedLang); } } catch (e) { /* storage unavailable — non-fatal */ }
        window.location.href = '/';
      } else {
        localStorage.setItem("lastUserActivity", now);
      }
    }, specialLogout[role] * 60 * 1000);
  };

  clearLogoutTimer = () => {
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  };

  render() {
    return (
      <Router basename='/'>
        <NetworkDetector />
        {/* Resets scroll to top on every route change — must be inside
            <Router> (uses useLocation). Mounted exactly once. */}
        <ScrollToTop />
        <Switch>
          <Route path='/PreScreenQues' component={PreScreenQuesPage} />
          <Route path='/PartnerPreScreenQues' component={PartnerPreScreenQuesPage} />
          <Route path='/surveys/completed' component={SurveyCompletedCallBackPage} />
          <Route path='/surveys/speederLimitTerminated' component={SurveySpeederLimitTerminate} />
          <Route path='/surveys/overquota' component={SurveyOverQuotaCallbackPage} />
          <Route path='/surveys/terminate' component={SurveyTerminatedCallbackPage} />
          <Route path='/surveys/qualityterminate' component={SurveyQualityTerminatedCallbackPage} />
          <Route path='/surveys/:surveyId/:userId' component={SurveyEntry} />
          <Route path='/partner' component={PartnerSurvey} />
          <Route path='/referrals/view/:userId' component={LinkViewWithState} />
          <Route path="/auth" component={() => <ErrorBoundary><AuthRouter /></ErrorBoundary>} />
          <Route path="/survey-attempted-message" component={SurveyAttemptMessageWithState} />
          <Route path="/survey-unavailable-message" component={SurveyUnavailableMessageWithState} />
          <Route path="/verify-screen" component={VerificationEmailScreensWithState} />
          {/* <Route path='/privacy-policy' component={PrivacyPolicy} /> */}
          <Route path='/terms-hi' component={TermsHi} />
          <Route path='/terms-jp' component={TermsJp} />
          <Route path='/terms' component={Terms} />
          <Route path='/privacy-policy-hi' component={PrivacyPolicyHi} />
          <Route path='/privacy-policy-jp' component={PrivacyJp} />
          <Route path='/privacy-policy' component={Privacy} />
          <Route path='/faq' component={Faq} />
          <Route path='/faq-hi' component={FaqHi} />
          <Route path='/survey-hold' component={SurveyHold} />
          <Route path='/partner-survey-hold' component={PartnerSurveyHold} />
          <Route path='/support' component={Support} />
          <Route path='/support-hi' component={Support_hi} />
          <Route path='/support-jp' component={SupportJp} />
          <Route path='/rewards-program-terms-hi' component={RewardsProgramTermsHi} />
          <Route path='/rewards-program-terms-jp' component={RewardsProgramTermsJp} />
          <Route path='/rewards-program-terms' component={RewardsProgramTerms} />
          <Route path='/cookie-policy-hi' component={CookiePolicyHi} />
          <Route path='/cookie-policy-jp' component={CookiePolicyJp} />
          <Route path='/cookie-policy' component={CookiePolicy} />
          <Route path='/reset-password/:token' component={ResetPasswordFormWithState} />
          <PrivateRouteWithRedux path="/admin" component={() => <ErrorBoundary><Admin /></ErrorBoundary>} />
          <PrivateRouteWithRedux path="/" component={() => <ErrorBoundary><Admin /></ErrorBoundary>} />
        </Switch>
      </Router>
    );
  }
}

const mapStateToProps = (state) => ({
  adminUser: state.adminUser,
});

const AppWithState = connect(mapStateToProps)(App);

export { AppWithState as App }
